import gql from "graphql-tag";
import xlsx from "xlsx/dist/xlsx.full.min.js";

function yesNo(value) {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return "";
}

function num(value) {
    if (value === null || value === undefined || value === "") return "";
    return value;
}

function pageInfoFields() {
    return `
        paginatorInfo {
            currentPage
            lastPage
            perPage
            total
            hasMorePages
        }
    `;
}

function listQuery(root, fields, extraArgs) {
    var args = "$first: Int!, $page: Int!, $entity: String";
    var callArgs = "first: $first, page: $page, entity: $entity";
    if (extraArgs && extraArgs.indexOf("campus") !== -1) {
        args += ", $campus: String";
        callArgs += ", campus: $campus";
    }
    return gql`
        query LibraryExport_${root}(${args}) {
            ${root}(${callArgs}) {
                error
                message
                ${pageInfoFields()}
                data {
                    ${fields}
                }
            }
        }
    `;
}

var SETTINGS_QUERY = gql`
    query LibraryExport_librarySettings($entity: String!) {
        librarySettings(entity: $entity) {
            error
            message
            data {
                id
                entity
                portal_url
                portal_label
                as_of
                digital_total
                digital_scope
            }
        }
    }
`;

var SHEETS = [
    {
        key: "Settings",
        sheetName: "Settings",
        singleRoot: "librarySettings",
        singleQuery: SETTINGS_QUERY,
        mapRow: function (item) {
            return {
                "Portal URL": item.portal_url || "",
                "Portal Label": item.portal_label || "",
                "As Of": item.as_of || "",
                "Digital Total": num(item.digital_total),
                "Digital Scope": item.digital_scope || "",
            };
        },
    },
    {
        key: "Databases",
        sheetName: "Databases",
        root: "libraryDatabases",
        withCampus: true,
        fields:
            "id name description access subscription_label status has_ai shared_with scope campus",
        mapRow: function (item) {
            return {
                Name: item.name || "",
                Description: item.description || "",
                Access: item.access || "",
                "Subscription Label": item.subscription_label || "",
                Status: item.status || "",
                "Has AI": yesNo(item.has_ai),
                "Shared With": item.shared_with || "",
                Scope: item.scope || "",
                Campus: item.campus || "",
            };
        },
    },
    {
        key: "DigitalTypes",
        sheetName: "Digital Types",
        root: "libraryDigitalHoldingTypes",
        withCampus: false,
        fields: "id key label total",
        mapRow: function (item) {
            return {
                Key: item.key || "",
                Label: item.label || "",
                Total: num(item.total),
            };
        },
    },
    {
        key: "DigitalProviders",
        sheetName: "Digital Providers",
        root: "libraryDigitalHoldingProviders",
        withCampus: false,
        fields: "id name total",
        mapRow: function (item) {
            return {
                Name: item.name || "",
                Total: num(item.total),
            };
        },
    },
    {
        key: "PrintCategories",
        sheetName: "Print Categories",
        root: "libraryPrintCategories",
        withCampus: false,
        fields: "id key label aa_count ad_count",
        mapRow: function (item) {
            var aa = item.aa_count === null || item.aa_count === undefined ? 0 : item.aa_count;
            var ad = item.ad_count === null || item.ad_count === undefined ? 0 : item.ad_count;
            return {
                Key: item.key || "",
                Label: item.label || "",
                AA: num(item.aa_count),
                AD: num(item.ad_count),
                Total: aa + ad,
            };
        },
    },
    {
        key: "Facilities",
        sheetName: "Facilities",
        root: "libraryFacilities",
        withCampus: true,
        fields: "id campus key label value icon",
        mapRow: function (item) {
            return {
                Campus: item.campus || "",
                Key: item.key || "",
                Label: item.label || "",
                Value: num(item.value),
                Icon: item.icon || "",
            };
        },
    },
];

var queryCache = {};

function getListQuery(root, fields, withCampus) {
    var cacheKey = root + "|" + fields + "|" + (withCampus ? "c" : "n");
    if (!queryCache[cacheKey]) {
        queryCache[cacheKey] = listQuery(
            root,
            fields,
            withCampus ? "campus" : ""
        );
    }
    return queryCache[cacheKey];
}

async function fetchSettings(apollo, entity) {
    var response = await apollo.query({
        query: SETTINGS_QUERY,
        variables: { entity: entity },
        fetchPolicy: "network-only",
    });
    var payload =
        response && response.data && response.data.librarySettings
            ? response.data.librarySettings
            : null;
    if (!payload || payload.error) {
        throw new Error(
            (payload && payload.message) ||
                "Failed to fetch librarySettings for export."
        );
    }
    return payload.data ? [payload.data] : [];
}

async function fetchAllPages(apollo, root, fields, withCampus, entity, campus) {
    var rows = [];
    var page = 1;
    var hasMore = true;
    var query = getListQuery(root, fields, withCampus);

    while (hasMore) {
        var variables = {
            first: 500,
            page: page,
            entity: entity,
        };
        if (withCampus) {
            variables.campus = campus || null;
        }

        var response = await apollo.query({
            query: query,
            variables: variables,
            fetchPolicy: "network-only",
        });

        var payload =
            response && response.data && response.data[root]
                ? response.data[root]
                : null;

        if (!payload || payload.error) {
            throw new Error(
                (payload && payload.message) ||
                    "Failed to fetch " + root + " for export."
            );
        }

        var data = payload.data || [];
        if (data.length) {
            rows.push.apply(rows, data);
        }

        hasMore = !!(
            payload.paginatorInfo && payload.paginatorInfo.hasMorePages
        );
        page += 1;
        if (page > 100) break;
    }

    return rows;
}

function safeSheetName(name) {
    var cleaned = String(name || "Sheet")
        .replace(/[\\/?*[\]]/g, " ")
        .trim();
    if (!cleaned) cleaned = "Sheet";
    return cleaned.substring(0, 31);
}

export function getTotalSheetCount() {
    return SHEETS.length;
}

/**
 * Export all library sections into one multi-sheet Excel workbook.
 * @param {object} apollo - Vue Apollo client (this.$apollo)
 * @param {string} entity
 * @param {string} campus
 * @param {function} [onProgress]
 * @returns {Promise<{ sheetCount: number, rowCount: number, fileName: string }>}
 */
export async function exportAllLibrarySections(apollo, entity, campus, onProgress) {
    var workbook = xlsx.utils.book_new();
    var sheetCount = 0;
    var rowCount = 0;
    var total = SHEETS.length;

    function reportProgress(sheetIndex, sheetName) {
        if (typeof onProgress === "function") {
            var percent = Math.round(((sheetIndex + 1) / total) * 100);
            onProgress({
                sheetIndex: sheetIndex,
                sheetName: sheetName,
                totalSheets: total,
                percent: percent,
            });
        }
    }

    for (var i = 0; i < SHEETS.length; i++) {
        var sheet = SHEETS[i];
        var mapped = [];
        var rawRows = [];

        reportProgress(i, sheet.sheetName);

        if (sheet.singleRoot) {
            rawRows = await fetchSettings(apollo, entity);
        } else {
            rawRows = await fetchAllPages(
                apollo,
                sheet.root,
                sheet.fields,
                !!sheet.withCampus,
                entity,
                campus
            );
        }

        for (var j = 0; j < rawRows.length; j++) {
            mapped.push(sheet.mapRow(rawRows[j]));
        }

        var worksheet =
            mapped.length > 0
                ? xlsx.utils.json_to_sheet(mapped)
                : xlsx.utils.aoa_to_sheet([["No records"]]);

        xlsx.utils.book_append_sheet(
            workbook,
            worksheet,
            safeSheetName(sheet.sheetName)
        );
        sheetCount += 1;
        rowCount += mapped.length;
    }

    var campusPart = campus || "ALL";
    var fileName = "ADPOLY_" + campusPart + "_Library_All_Sections.xlsx";
    xlsx.writeFile(workbook, fileName);

    return {
        sheetCount: sheetCount,
        rowCount: rowCount,
        fileName: fileName,
    };
}
